import { Text } from '@chakra-ui/react';
import { useUnit } from 'effector-react';

import { PageHeader } from '@m/components-ui/page-header';
import { SUPPORT_URL } from '@m/constants/Contacts';
import OnboardingHeader from 'apps/customer/containers/OnboardingFlow/components/OnboardingHeader';

import { onboardingSessionStore } from './onboardingSession';
import { OnboardingStep } from './OnboardingStep';
import { stepOrderFor } from './stepOrder';
import { STEPPER_VISIBLE_STEPS } from './stepRoutes';

import type { OnboardingVariant } from '../deriveVariant';
import type { StepConfig } from '@m/components-ui/stepper';

export type ContainerHeaderProps = {
  /** Set only while a step has registered its own back behavior (currently just
   *  `VehicleSaveOptionsFlow`, which only mounts on the vehicle step's second sub-step — "go
   *  back to plate entry"). */
  backOverride: { fn: () => void } | null;
  currentStep: OnboardingStep;
  /** Goes back to the name/email form, which stays editable. Only used where that form comes
   *  before the vehicle step. */
  onBackToContact: () => void;
  onClose: () => void;
  variant: OnboardingVariant;
};

const NEW_ACCOUNT_STEPPER_LENGTH = 4;

/**
 * The Figma stepper counts the vehicle's two sub-steps separately: [plate, save vehicle, payment,
 * last step]. The last step is name/email for zone-agnostic; individual subscriptions leave it
 * unlit because their review page has its own title-only header. Enterprise and returning members
 * have no stepper in the mocks.
 */
function newAccountStepperIndex({
  hasBackOverride,
  step,
  variant,
}: {
  hasBackOverride: boolean;
  step: OnboardingStep;
  variant: OnboardingVariant;
}): number | null {
  if (step === OnboardingStep.AddVehicle) return hasBackOverride ? 1 : 0;
  if (step === OnboardingStep.AddPayment) return 2;
  if (variant === 'agnostic' && step === OnboardingStep.AddEmail) return 3;
  return null;
}

function stepperFor({
  hasBackOverride,
  isNewAccount,
  order,
  step,
  variant,
}: {
  hasBackOverride: boolean;
  isNewAccount: boolean;
  order: readonly OnboardingStep[];
  step: OnboardingStep;
  variant: OnboardingVariant;
}): StepConfig[] | undefined {
  if (variant === 'agnostic' || variant === 'individualSub') {
    if (!isNewAccount) return undefined;
    const active = newAccountStepperIndex({ hasBackOverride, step, variant });
    if (active === null) return undefined;
    return Array.from({ length: NEW_ACCOUNT_STEPPER_LENGTH }, (_, index) => ({
      isActive: index === active,
      isComplete: index < active,
    }));
  }
  if (variant === 'enterpriseSub' || !STEPPER_VISIBLE_STEPS.has(step)) return undefined;

  const stepIndex = order.indexOf(step);
  if (stepIndex < 0) return undefined;
  return order.map((orderStep, index) => ({
    isActive: index === stepIndex,
    isComplete: index < stepIndex,
  }));
}

export default function ContainerHeader({
  backOverride,
  currentStep,
  onBackToContact,
  onClose,
  variant,
}: ContainerHeaderProps) {
  const { isReturningUser } = useUnit(onboardingSessionStore);
  const isNewAccount = isReturningUser === false;
  const order = stepOrderFor({ isNewAccount, variant });

  // Name/email is editable as often as the user likes, so the vehicle step can return to it when it
  // was collected first. Nothing else is: the vehicle and payment method are saved on Continue.
  const isSubscription = variant === 'enterpriseSub' || variant === 'individualSub';
  const canGoBackToContact =
    isSubscription &&
    currentStep === OnboardingStep.AddVehicle &&
    order.indexOf(OnboardingStep.AddEmail) >= 0 &&
    order.indexOf(OnboardingStep.AddEmail) < order.indexOf(OnboardingStep.AddVehicle);

  // The review page has no logo, close or help in Figma — just its title.
  if (currentStep === OnboardingStep.ReviewSubscription) {
    return (
      <PageHeader
        centerContent={<Text variant="MainBodyDemiBold">Review subscription plan</Text>}
      />
    );
  }

  const onBack = backOverride ? () => backOverride.fn() : onBackToContact;
  const showsBack = !!backOverride || canGoBackToContact;

  return (
    <OnboardingHeader
      hideBack={!showsBack}
      showClose={!showsBack}
      onClose={onClose}
      onBack={showsBack ? onBack : undefined}
      onHelp={() => window.open(SUPPORT_URL, '_blank', 'noopener,noreferrer')}
      reserveStepperSpace
      steps={stepperFor({
        hasBackOverride: !!backOverride,
        isNewAccount,
        order,
        step: currentStep,
        variant,
      })}
    />
  );
}
